import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import Setup2faModal from './settings/2fa-auth/2fa-setup-modal';

import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import { useSnackbarContext } from '~c/components/base/snackbar';

interface Props extends React.PropsWithChildren {}

/**
 * This component enforces 2FA setup when:
 * 1. The user's organization has enforce2FA enabled
 * 2. The user does not have 2FA enabled
 *
 * It blocks access to the app until 2FA is set up.
 */
const TwoFactorEnforcementGuard: React.FC<Props> = ({ children }) => {
  const { userProfile, refetch: refetchUserProfile } = useUserProfile();
  const Snackbar = useSnackbarContext();
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [totpUrl, setTotpUrl] = useState<string>('');
  const [totpTextCode, setTotpTextCode] = useState<string>('');
  const hasGeneratedToken = useRef(false);

  // Always fetch fresh enforcement status — cache-first would serve stale
  // enforce2FA=false after an admin enables enforcement mid-session.
  const { data: organizationContexts, loading: loadingOrgContexts } =
    GQL.useUserOrganizationContextsQuery({
      skip: !userProfile?.email,
      fetchPolicy: 'network-only',
    });

  const { data: totpStatusData, loading: loadingTotpStatus } =
    GQL.useTotpStatusQuery({
      skip: !userProfile?.email,
      fetchPolicy: 'network-only',
    });

  const [generateTotpToken, { loading: isGenerating }] =
    GQL.useGenerateTotpTokenMutation({
      onCompleted: (data) => {
        if (data.generateTotpToken) {
          setTotpUrl(data.generateTotpToken.url ?? '');
          setTotpTextCode(data.generateTotpToken.textCode ?? '');
        }
      },
      onError: (error: any) => {
        // Allow the effect to retry on the next render cycle.
        hasGeneratedToken.current = false;
        Snackbar.open(`Failed to generate 2FA code: ${error.message}`, {
          showOk: true,
        });
      },
    });

  const [verifyTotpToken, { loading: isVerifying }] =
    GQL.useVerifyTotpTokenMutation({
      onCompleted: (data) => {
        if (data.verifyTotpToken?.user) {
          Snackbar.open(
            '2FA has been successfully enabled. You can now access the application.',
            {
              showOk: true,
            }
          );
          refetchUserProfile();
          setShowEnforcementModal(false);
        }
      },
      onError: (error: any) => {
        Snackbar.open(`Failed to enable 2FA: ${error.message}`, {
          showOk: true,
        });
      },
      refetchQueries: [
        {
          query: GQL.TotpStatusDocument,
        },
      ],
      awaitRefetchQueries: true,
    });

  // Find user's current organization context
  const currentOrgContext =
    organizationContexts?.userOrganizationContexts?.find(
      (ctx: any) => ctx.organization._id === userProfile?.defaultOrganizationId
    );

  const isOrg2FAEnforced = currentOrgContext?.organization?.enforce2FA ?? false;
  const isUser2FAEnabled =
    totpStatusData?.totpStatus?.otpEnabled ?? userProfile?.otpEnabled ?? false;
  const isSystemAdmin = userProfile?.isSystemAdmin ?? false;

  // System admins should bypass 2FA enforcement so they can manage the setting
  const shouldEnforce2FA =
    isOrg2FAEnforced && !isUser2FAEnabled && !isSystemAdmin;

  useEffect(() => {
    if (
      !loadingOrgContexts &&
      !loadingTotpStatus &&
      shouldEnforce2FA &&
      !hasGeneratedToken.current &&
      organizationContexts &&
      totpStatusData
    ) {
      // Mark as in-flight before the async call. The onError handler resets this
      // to false if the mutation fails so the next render can retry.
      hasGeneratedToken.current = true;
      generateTotpToken().then((result) => {
        // Only open the modal after the token is confirmed generated.
        if (result?.data?.generateTotpToken) {
          setShowEnforcementModal(true);
        } else {
          // Mutation returned no data (e.g. null response); allow retry.
          hasGeneratedToken.current = false;
        }
      });
    }
  }, [
    shouldEnforce2FA,
    organizationContexts,
    totpStatusData,
    loadingOrgContexts,
    loadingTotpStatus,
  ]);

  const enableOTPHandler = async (otpToken: number) => {
    try {
      const result = await verifyTotpToken({
        variables: {
          input: {
            otpToken,
          },
        },
      });
      return result.data;
    } catch (error: any) {
      return { error };
    }
  };

  // While checking enforcement status, show a neutral loading screen so we
  // never flash app content that may be protected.
  if (loadingOrgContexts || loadingTotpStatus) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
          bgcolor: 'background.default',
          color: 'text.primary',
          gap: 2,
        }}
        aria-busy
        aria-label="Loading"
      >
        <CircularProgress size={48} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (shouldEnforce2FA) {
    // Show a loading screen while the TOTP QR code is being generated so the
    // user never sees a blank page between enforcement detection and modal ready.
    if (!showEnforcementModal || !totpUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            width: '100%',
            bgcolor: 'background.default',
            color: 'text.primary',
            gap: 2,
          }}
          aria-busy
          aria-label="Preparing 2FA setup"
        >
          <CircularProgress size={48} color="primary" />
          <Typography variant="body2" color="text.secondary">
            Preparing two-factor authentication setup…
          </Typography>
        </Box>
      );
    }

    return (
      <Setup2faModal
        generateResp={{
          url: totpUrl,
          textCode: totpTextCode,
        }}
        open
        handleClose={() => {
          // Do nothing - user cannot close this modal
        }}
        enableOTPHandler={enableOTPHandler}
        isVerifyTotp={isVerifying}
        isEnforced
        enforcementMessage="Your organization requires two-factor authentication. You must set this up to continue."
      />
    );
  }

  // User has 2FA enabled, org doesn't enforce it, or user is system admin - allow access
  return <>{children}</>;
};

export default TwoFactorEnforcementGuard;
